"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import { Box, Card, CardContent, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";

const ReactECharts = dynamic(() => import("echarts-for-react"), {
  ssr: false,
});

type PieDatum = {
  asn_type: string;
  count: number;
};

type UserStatisticsPieChartProps = {
  data: PieDatum[];
};

const ASN_LABELS: Record<string, string> = {
  business: "Business",
  consumer_vpn: "Consumer VPN",
  edu: "Education",
  gov: "Government",
  isp: "Others",
  unknown: "Unknown",
};

const PIE_COLORS = [
  "#5470C6",
  "#91CC75",
  "#EE6666",
  "#FAC858",
  "#73C0DE",
  "#9A60B4",
];

const asReadableLabel = (asnType: string) => ASN_LABELS[asnType] ?? asnType;

export default function UserStatisticsPieChart({
  data,
}: UserStatisticsPieChartProps) {
  const option = useMemo<EChartsOption>(() => {
    const pieData = [...data]
      .filter((item) => Number(item.count) > 0)
      .sort((a, b) => b.count - a.count)
      .map((item, index) => ({
        name: asReadableLabel(item.asn_type),
        value: item.count,
        itemStyle: { color: PIE_COLORS[index % PIE_COLORS.length] },
      }));

    return {
      tooltip: {
        trigger: "item",
        formatter: (params: unknown) => {
          const typed = params as {
            name: string;
            value: number;
            percent: number;
          };
          // If percent is < 1, only round to the nearest 0.1
          return `${typed.name}: ${typed.value} (${typed.percent > 1 ? Math.round(typed.percent) : Math.round(typed.percent * 10) / 10}%)`;
        },
      },
      legend: {
        left: "center",
        bottom: 8,
        orient: "horizontal",
        itemWidth: 14,
        itemHeight: 10,
        itemGap: 18,
        textStyle: {
          fontSize: 14,
          fontWeight: 500,
          color: "#4b5563",
        },
      },
      series: [
        {
          type: "pie",
          radius: "64%",
          center: ["50%", "50%"],
          label: {
            show: true,
            formatter: "{b}",
            fontSize: 14,
            fontWeight: 500,
            color: "#5b5f65",
            distance: 10,
            lineHeight: 16,
            padding: [2, 4],
          },
          labelLine: {
            length: 12,
            length2: 40,
          },
          data: pieData,
        },
      ],
    };
  }, [data]);

  return (
    <Card>
      <CardContent
        sx={{
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 5 },
          "&:last-child": {
            pb: { xs: 2, md: 5 },
          },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "70%" },
            textAlign: "center",
            mx: "auto",
          }}
        >
          <Typography
            sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#4c4f56" }}
          >
            Our Users
          </Typography>
        </Box>

        <ReactECharts
          option={option}
          style={{ height: 420, width: "100%" }}
          notMerge
        />

        {/* <Box
          component="img"
          src="/SDF-Logo.png"
          alt="Sovereign Debt Forum logo"
          sx={{
            width: { xs: 100, md: 140 },
            height: "auto",
            opacity: 0.85,
            display: { xs: "none", md: "block" },
          }}
        /> */}
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            justifyContent: "space-between",
            gap: 2,
            mb: 1,
          }}
        >
          <Box
            component="img"
            src="/SDF-Logo.png"
            alt="Sovereign Debt Forum logo"
            sx={{
              width: { xs: 92, sm: 130 },
              height: "auto",
              flexShrink: 0,
              opacity: 0.7,
            }}
          />
          <Typography
            sx={{
              mt: 1,
              fontSize: "0.7rem",
              color: "#626367",
              textAlign: "left",
              lineHeight: "1rem",
            }}
          >
            User analytics based on IP geolocation.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
